// ── WHEN A RESEARCH ASK FAILS (busy-my-web#514) ──────────────────────────────────────────────
// Saffet, as a brand-new user, asked Home's chat "I like to bake a cake" and got "Sorry — something
// went wrong. Please try again." — research.js's catch-all, written once BOTH the streamed ask and
// the one-shot POST v1/ai/ask had failed with anything but a 429. He tried again and it answered:
// the classic shape of a cold AI service on a new user's first call. Two things follow from that,
// and both live here as plain functions so the store only has to call them:
//
//  1. the one-shot ask is retried ONCE, after a short pause, before anything is shown — but only
//     for failures a second try can plausibly cure (no response at all, a 5xx; a timeout no longer,
//     #695 below). A 4xx is
//     the server refusing THIS request, and asking again would just be refused again; a 429 is the
//     daily budget and keeps its own handling in research.js untouched.
//  2. when it still fails, the turn says what the BFF said, when the BFF said something a person
//     can read — its ExceptionMiddleware answers with an ApiError (`UserMessage`, serialised
//     PascalCase by Newtonsoft; camelCase accepted too), and the controller guards answer
//     `{ message }` with a 400. Anything else (a bare network error, a stack trace, the
//     middleware's own "Oops! something went wrong") falls back to the generic sentence, because
//     it says nothing the generic sentence doesn't.

// ── WHEN AN ASK NEVER ANSWERS (busy-my-web#695) ──────────────────────────────────────────────
// Saffet: "I write a search but sometimes I don't get a reply — what I searched for shows in the
// right hand corner". The question (blue, on the right) was all the turn ever drew, because nothing
// on the way had a clock: no axios timeout, no limit on how long the SSE stream may stay silent, and
// an "answer" with no words in it was painted as one. Three rules, all below, so every ask either
// answers or says it couldn't within ~45 s:
//
//  3. the one-shot asks carry ASK_TIMEOUT_MS, and the stream is ended after STREAM_IDLE_MS of
//     silence — a quiet stream is a stalled one (ai-srv speaks every second or two while it works);
//  4. a timeout is NOT retried: the server has already spent the budget once, and a second try would
//     double the wait the user is staring at. A 504 is the server's own timeout and counts the same;
//  5. an answer with nothing in it (`isUnanswered`) is a failure, not a blank turn.
// The failed turn says ASK_FAILED_TEXT — the busy-main-bff 504's own sentence — and keeps its retry.

/** The sentence shown when the server gave us nothing better (the issue's wording, #695). */
export const ASK_FAILED_TEXT = 'Busy couldn\'t answer — try again.'
// A stream that went silent part-way (#695): what it wrote stays, with this line under it.
export const ASK_CUT_SHORT_TEXT = 'Busy stopped before finishing this answer — ask again for the rest.'

/** How long to wait before the one retry — long enough for a cold pod to finish waking. */
export const ASK_RETRY_DELAY_MS = 800

/** The longest a one-shot ask is waited on (#695): the BFF gives up on ai-srv at 40 s. */
export const ASK_TIMEOUT_MS = 45000

/** The longest the web-ask stream may say nothing before the turn is called failed (#695). */
export const STREAM_IDLE_MS = 30000

/** True when the ask ran out of time — ours (axios `timeout`) or the server's (a 504). */
export function isTimeoutError(e) {
  if (e?.response?.status === 504) return true
  if (e?.response) return false
  return e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT' || /timeout/i.test(String(e?.message || ''))
}

/**
 * True when a response that "succeeded" carries nothing a person could read (#695): no answer text,
 * no blocks, no sources of either kind. Drawn as it came, it was the bare question Saffet saw.
 */
export function isUnanswered(data) {
  if (!data || typeof data !== 'object') return true
  if (typeof data.answer === 'string' && data.answer.trim()) return false
  const any = (list) => Array.isArray(list) && list.length > 0
  return !any(data.blocks) && !any(data.sources) && !any(data.webSources)
}

// Longer than this and it is a dump, not a message (a dev-mode exception, an HTML error page).
const MAX_MESSAGE_LENGTH = 240

// The server's own catch-alls: showing them would swap one blanket sentence for another.
// …and the BFF's bare relay of a downstream status ("Downstream service error: InternalServerError"),
// which names a mechanism, not a reason (#695).
const GENERIC = /something went wrong|server error|downstream service error/i

/** True when a failed ask is worth one more try: no answer at all, or a 5xx — but never a timeout
 *  (#695): that budget is already spent, and asking again doubles the wait. */
export function isRetryableAskError(e) {
  if (isTimeoutError(e)) return false
  const status = e?.response?.status
  if (!status) return true
  return status === 408 || status >= 500
}

function readable(text) {
  if (typeof text !== 'string') return ''
  const clean = text.trim()
  if (!clean || clean.length > MAX_MESSAGE_LENGTH || GENERIC.test(clean)) return ''
  // A trace or markup is not a sentence for a person.
  if (/\bat\s+\S+\(|<\/?[a-z][^>]*>/i.test(clean)) return ''
  return clean
}

/**
 * The text a failed ask's turn shows: the BFF's user-facing message when it carried a usable one,
 * otherwise `fallback`. `UserMessage` is the one written for people; a plain `message` is read only
 * off a 4xx (a controller guard's sentence) — on a 5xx it is the exception's own text.
 */
export function askErrorText(e, fallback = ASK_FAILED_TEXT) {
  const data = e?.response?.data
  if (!data || typeof data !== 'object') return fallback
  const user = readable(data.userMessage ?? data.UserMessage)
  if (user) return user
  const status = e?.response?.status
  if (status >= 400 && status < 500) {
    const message = readable(data.message ?? data.Message)
    if (message) return message
  }
  return fallback
}

const pause = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Run `call`, and when it fails with a retryable error run it once more after `delayMs`.
 * `cancelled()` is checked after the pause, so a turn the user stopped in the meantime is not
 * asked again — the original error is rethrown and the caller's own stopped-check drops it.
 */
export async function withOneRetry(call, { delayMs = ASK_RETRY_DELAY_MS, cancelled = () => false, sleep = pause } = {}) {
  try {
    return await call()
  } catch (e) {
    if (!isRetryableAskError(e)) throw e
    await sleep(delayMs)
    if (cancelled()) throw e
    return await call()
  }
}
